import DialogModal from 'components/modal/dialog-modal';
import { IDeleteAudienceDialog } from './types';
import { AudienceDeletionDialogHeader } from './components/audience-deletion-dialog-header';
import { DialogContainer } from '../shared/dialog';
import { Autocomplete, TextField } from '@mui/material';
import PopupIcon from 'assets/icons/vector-bottom-icon.svg';
import { useDeleteCustomAudienceMutation } from 'redux-store/api/activity-feed';
import { styles } from './styles';
import { useState } from 'react';
import { SourceEnum, TSourcedSpecificUser } from '../types';
import { AudienceDeletionDialogFooter } from './components/audience-deletion-dialog-footer';
import useToast from 'hooks/useToast';

export const DeleteAudienceDialog = ({
  availableProfiles,
  projectId,
  audienceToDelete,
  activeAudience,
  onSetActiveAudience,
  onSetLatestPostAudience,
  onCancel,
}: IDeleteAudienceDialog) => {
  const showToast = useToast();

  const [deleteCustomAudience, { isLoading }] = useDeleteCustomAudienceMutation();

  const { byRole = [], customGroups = [] } = availableProfiles || {};
  const [newSelectedAudience, setNewSelectedAudience] = useState<TSourcedSpecificUser | null>(null);

  const audienceOptions: TSourcedSpecificUser[] = [...byRole, ...customGroups].filter(
    (audience) => audience.id !== audienceToDelete.id
  );

  const handleOptionChange = (_event: React.SyntheticEvent, value: TSourcedSpecificUser) => {
    setNewSelectedAudience(value);
  };

  const handleCancel = () => {
    if (isLoading) return;
    onCancel();
  };

  const onSuccess = () => {
    // If no audience is selected for deletion, cancel the action
    if (!audienceToDelete.isSelected) return onCancel();

    // Destructure to get source and id from both deleted and newly selected audience
    const { source: deletionSource, id: deletionId } = audienceToDelete;
    const { source: newSource, id: newId } = newSelectedAudience;

    // Create an updated audience object with the deleted audience removed
    const updatedAudience = {
      ...activeAudience,
      [deletionSource]: activeAudience[deletionSource].filter(
        (profile) => profile.id !== deletionId // Remove the profile by id
      ),
    };

    // Check if the new audience is already selected in the target source
    const isNewAudienceSelected = activeAudience[newSource].some((profile) => profile.id === newId);

    // If the new audience is already selected, no need to add it again, just update the state
    if (isNewAudienceSelected) {
      onSetActiveAudience(updatedAudience);
      onSetLatestPostAudience(updatedAudience);
    } else {
      // If the new audience is not selected, add it to the respective source
      const updatedWithNewAudience = {
        ...updatedAudience,
        [newSource]: [...updatedAudience[newSource], newSelectedAudience],
      };
      onSetActiveAudience(updatedWithNewAudience);
      onSetLatestPostAudience(updatedWithNewAudience);
    }

    // Cancel the action after processing
    onCancel();
  };

  const onConfirm = () => {
    const groupIdToAssign =
      newSelectedAudience?.source === SourceEnum.CUSTOM_GROUPS ? newSelectedAudience.id : null;
    const roleIdToAssign =
      newSelectedAudience?.source === SourceEnum.BY_ROLE ? newSelectedAudience.id : null;

    deleteCustomAudience({
      projectId,
      groupId: audienceToDelete.id,
      groupIdToAssign,
      roleIdToAssign,
    })
      .unwrap()
      .then(onSuccess)
      .catch(() => {
        showToast({
          message: 'Something went wrong!',
          type: 'error',
        });
      });
  };

  return (
    <DialogModal
      withoutPadding
      maxWidth="650px"
      open={true}
      withCloseButton={false}
      handleClose={handleCancel}
    >
      <AudienceDeletionDialogHeader />
      <DialogContainer>
        <Autocomplete<TSourcedSpecificUser, false, true, false>
          popupIcon={<PopupIcon />}
          isOptionEqualToValue={(option, val) => option.id === val.id}
          disableClearable
          getOptionLabel={(option) => option.groupName}
          onChange={handleOptionChange}
          options={audienceOptions}
          value={newSelectedAudience}
          renderInput={(params) => (
            <TextField
              sx={styles.textField}
              InputProps={{ endAdornment: true }}
              {...params}
              placeholder="Select audience"
              variant="outlined"
              inputProps={{
                ...params.inputProps,
                autoComplete: 'off',
              }}
            />
          )}
        />
        <AudienceDeletionDialogFooter
          confirmDisabled={!newSelectedAudience}
          isLoading={isLoading}
          onCancel={handleCancel}
          onConfirm={onConfirm}
        />
      </DialogContainer>
    </DialogModal>
  );
};
